import type { ProviderKey } from "@versatile-credit-apply/omni-apply-client"
import { extractProviderKey } from "@versatile-credit-apply/sdk"
import {
  computed,
  type ComputedRef,
  type MaybeRefOrGetter,
  ref,
  toValue,
  watchEffect,
} from "vue"

// Use vite to create loading bundles or png
const MerchantAssets = import.meta.glob(["./group/**/**.(svg|png|ico)"], {
  query: "?url",
  import: "default",
})

const findMerchantAsset = (group: string, file: string) =>
  MerchantAssets[`./group/${group}/${file}.png`] ??
  MerchantAssets[`./group/${group}/${file}.svg`] ??
  MerchantAssets[`./group/${group}/${file}.ico`]

const findMerchantProviderAsset = (
  group: string,
  providerKey: ProviderKey,
  file: string
) =>
  MerchantAssets[`./group/${group}/${providerKey}/${file}.png`] ??
  MerchantAssets[`./group/${group}/${providerKey}/${file}.svg`] ??
  MerchantAssets[`./group/${group}/${providerKey}/${file}.ico`]

const useMerchantAsset = (
  group: MaybeRefOrGetter<string | undefined | null>,
  file: string,
  providerKey?: MaybeRefOrGetter<ProviderKey | undefined>
): ComputedRef<string | undefined> => {
  const asset = ref<string>()

  watchEffect(async () => {
    const groupId = toValue(group)
    const provider = toValue(providerKey)

    if (groupId) {
      let loader: (() => Promise<unknown>) | undefined
      if (provider) {
        loader = findMerchantProviderAsset(
          groupId,
          extractProviderKey(provider),
          file
        )
      } else {
        loader = findMerchantAsset(groupId, file)
      }

      if (loader) {
        asset.value = (await loader()) as string
      }
    }
  })

  return computed(() => asset.value)
}

export type UseMerchantAsset = (
  group: MaybeRefOrGetter<string | undefined | null>,
  providerKey?: MaybeRefOrGetter<ProviderKey | undefined>
) => ComputedRef<string | undefined>

export const useMerchantFaviconAsset: UseMerchantAsset = (group) =>
  useMerchantAsset(group, "favicon")

export const useMerchantLogoAsset: UseMerchantAsset = (group) =>
  useMerchantAsset(group, "logo")

export const useMerchantMpqAsset: UseMerchantAsset = (group, provider) =>
  useMerchantAsset(group, "mpq", provider)
